/**
 * Every kind of file the team uploads, and how the Warehouse shows each one
 * (Aziz 2026-09-28: "all kinds of files supported throughout the system").
 * One place, so the card, the viewer and the uploaders agree.
 */
import type { MediaAsset } from '@/lib/queries';

export function extOf(name: string | null | undefined): string {
  const n = name ?? '';
  const i = n.lastIndexOf('.');
  return i > 0 ? n.slice(i + 1).toLowerCase() : '';
}

/** Camera RAW photos. No browser draws them; the server makes a JPEG preview. */
export const RAW_PHOTO_EXT = new Set(['arw', 'cr2', 'cr3', 'nef', 'nrw', 'dng', 'raf', 'orf', 'rw2', 'pef', 'srw', '3fr', 'iiq', 'x3f']);
/** Stills a browser cannot draw as they are (the server makes the preview). */
export const NEEDS_PREVIEW_EXT = new Set([...RAW_PHOTO_EXT, 'heic', 'heif', 'tif', 'tiff', 'bmp', 'psd', 'jp2', 'avif']);
/** Read as text in the viewer. */
export const TEXT_EXT = new Set(['xml', 'txt', 'csv', 'tsv', 'json', 'srt', 'vtt', 'md', 'log', 'ass', 'ssa', 'edl', 'xmp', 'fcpxml', 'yaml', 'yml', 'html', 'htm']);

/**
 * Operating system leftovers: macOS writes a "._name" twin of every file copied to a
 * non-Mac drive, plus .DS_Store; Windows writes Thumbs.db and desktop.ini. Aziz
 * 2026-09-29: "hide nothing, make everything readable", so they upload and stay like any
 * file; this only labels them, so a ._DSC.jpg is never taken for a photo.
 */
export function isJunkFile(name: string): boolean {
  const base = (name.split('/').pop() ?? name);
  const low = base.toLowerCase();
  return base.startsWith('._') || low === '.ds_store' || low === 'thumbs.db' || low === 'desktop.ini' || low === '.localized';
}

/** Word processor files. A .docx is converted on the server and read in place
 *  (api/app/doc_reader.py); the others show with a download (2026-10-15). */
export const DOC_EXT = new Set(['docx', 'docm', 'doc', 'odt', 'rtf', 'pages', 'wpd']);
const DOC_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-word.document.macroenabled.12',
  'application/msword', 'application/vnd.oasis.opendocument.text', 'application/rtf', 'text/rtf',
]);
/** Written documents a brief can come as: shown in the Brief documents block. */
const BRIEF_DOC_EXT = new Set([...DOC_EXT, 'pdf', 'txt', 'md', 'csv']);

export type ShowAs = 'video' | 'image' | 'preview-image' | 'pdf' | 'document' | 'text' | 'audio' | 'file';

/** How the viewer shows this asset. */
export function showAs(a: Pick<MediaAsset, 'filename' | 'content_type'>): ShowAs {
  const ct = (a.content_type ?? '').toLowerCase();
  const e = extOf(a.filename);
  if (isJunkFile(a.filename)) return 'file';
  if (ct.startsWith('video/') || ['mp4', 'mov', 'm4v', 'webm', 'mkv', 'avi', 'mts', 'm2ts', 'mxf'].includes(e)) return 'video';
  if (NEEDS_PREVIEW_EXT.has(e)) return 'preview-image';
  if (ct.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(e)) return 'image';
  if (ct === 'application/pdf' || e === 'pdf') return 'pdf';
  if (DOC_EXT.has(e) || DOC_TYPES.has(ct)) return 'document';
  if (ct.startsWith('audio/') || ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac', 'opus'].includes(e)) return 'audio';
  if (TEXT_EXT.has(e) || ct.startsWith('text/') || ct.endsWith('+xml') || ct === 'application/xml' || ct === 'application/json') return 'text';
  return 'file';
}

/** A document someone wrote to be read (Word, PDF, text, Markdown, CSV), not a picture or a clip. */
export function isBriefDocument(a: Pick<MediaAsset, 'filename' | 'content_type'>): boolean {
  if (isJunkFile(a.filename)) return false;
  const s = showAs(a);
  return s === 'pdf' || s === 'document' || BRIEF_DOC_EXT.has(extOf(a.filename));
}

/** Short label for a file-type badge: "ARW", "XML", "ZIP". */
export function typeBadge(a: Pick<MediaAsset, 'filename'>): string {
  return (extOf(a.filename) || 'file').toUpperCase().slice(0, 6);
}
